import { useEffect, useState } from 'react';
import { datadogLogs } from '@datadog/browser-logs';
import { useDecision } from '@optimizely/react-sdk';
import { useUniversalNavigationContext } from '../context/UniversalNavigationContext';
import {
  NEW_MEMBER_TIMER_DEPRECATION_FLAG,
  TIMER_DEPRECATION_DECISION_TIMEOUT,
} from '../helpers/timerDeprecation';

type TimerDeprecationDecision = {
  isDecisionReady: boolean;
  isTimerDeprecated: boolean;
};

/**
 * Resolves `new_member_timer_deprecation` once the Optimizely client is ready, or as off when it
 * is not ready within the timeout. The first resolved value is kept for the life of the component
 * and reported to the UniversalNavigationContext. Must be called inside the OptimizelyProvider.
 */
export function useTimerDeprecationDecision(): TimerDeprecationDecision {
  const { onTimerDeprecationDecided } = useUniversalNavigationContext();
  const [decision, clientReady, didTimeout] = useDecision(NEW_MEMBER_TIMER_DEPRECATION_FLAG, {
    timeout: TIMER_DEPRECATION_DECISION_TIMEOUT,
  });
  const [fallbackElapsed, setFallbackElapsed] = useState(false);
  const [resolvedDecision, setResolvedDecision] = useState<boolean | null>(null);

  // useDecision never times out when the client fails to initialize, so keep a timeout of our own.
  useEffect(() => {
    if (clientReady || didTimeout) {
      return undefined;
    }
    const timeoutId = setTimeout(
      () => setFallbackElapsed(true),
      TIMER_DEPRECATION_DECISION_TIMEOUT,
    );
    return () => clearTimeout(timeoutId);
  }, [clientReady, didTimeout]);

  useEffect(() => {
    if (resolvedDecision !== null) {
      return;
    }
    if (clientReady) {
      setResolvedDecision(!!decision?.enabled);
      return;
    }
    if (didTimeout || fallbackElapsed) {
      datadogLogs.logger.warn('Optimizely decision timed out; resolving as off', {
        flagKey: NEW_MEMBER_TIMER_DEPRECATION_FLAG,
      });
      setResolvedDecision(false);
    }
  }, [clientReady, didTimeout, fallbackElapsed, decision, resolvedDecision]);

  useEffect(() => {
    if (resolvedDecision !== null) {
      onTimerDeprecationDecided(resolvedDecision);
    }
  }, [resolvedDecision, onTimerDeprecationDecided]);

  return {
    isDecisionReady: resolvedDecision !== null,
    isTimerDeprecated: resolvedDecision === true,
  };
}

export default useTimerDeprecationDecision;
